import moment from 'moment'

export function getMinimumCheckoutDuration(checkoutCalendarDays: Record<string, App.CalendarDay>, selectedCheckIn?: string): number | undefined {
  if (!selectedCheckIn) return

  const durations = Object.keys(checkoutCalendarDays)
    .map(checkOut => moment(checkOut).diff(selectedCheckIn, 'days'))
    .filter(candidateDuration => candidateDuration > 0)

  return durations.length ? Math.min(...durations) : undefined
}

export type FixedFlashCalendarSelectionMode = 'inactive' | 'select-check-in' | 'select-check-out' | 'selected-range'

interface FixedFlashCalendarSelectionModeOptions {
  isFixedFlashLengthOfStayEnabled: boolean
  selectedCheckIn?: string
  selectedCheckOut?: string
  bundleOfferId?: string
}

interface FixedFlashCalendarDayStateOptions {
  day: App.CalendarDay
  selectedCheckIn?: string
  selectedCheckOut?: string
  checkoutCalendarDays: Record<string, App.CalendarDay>
  checkInCalendarDays: Record<string, App.CalendarDay>
  isFixedFlashLengthOfStayDateSelection: boolean
  minimumCheckoutDuration?: number
  maximumDuration?: number
}

interface FixedFlashCalendarDayPricingStateOptions {
  day: App.CalendarDay
  fallbackDuration?: number
  isBeforeSelectedCheckIn: boolean
  selectedCheckOut?: string
  checkoutCalendarDay?: App.CalendarDay
  checkInCalendarDay?: App.CalendarDay
}

interface FixedFlashCheckoutDateOptions {
  isFixedFlashLengthOfStayEnabled: boolean
  selectedCheckOut?: string
  fallbackCheckOut?: string
}

interface ClearUnavailableCheckInOptions {
  isFixedFlashLengthOfStayEnabled: boolean
  checkIn?: string
  checkOut?: string
  fetching?: boolean
  selectedDay?: App.CalendarDay
  hasOtherAvailability: boolean
}

interface FixedFlashFooterSelectedDayOptions {
  isFixedFlashLengthOfStayEnabled: boolean
  checkIn?: string
  checkOut?: string
  selectedDay?: App.CalendarDay
  fixedFlashLengthOfStaySelectedDay?: App.CalendarDay
}

export function getFixedFlashFooterSelectedDay(options: FixedFlashFooterSelectedDayOptions) {
  const shouldUseFixedFlashDay = options.isFixedFlashLengthOfStayEnabled && !!options.checkIn && !options.checkOut

  return shouldUseFixedFlashDay ? options.fixedFlashLengthOfStaySelectedDay : options.selectedDay
}

export function getFixedFlashCheckoutDate(options: FixedFlashCheckoutDateOptions) {
  return options.isFixedFlashLengthOfStayEnabled ? options.selectedCheckOut : options.fallbackCheckOut
}

export function shouldClearUnavailableCheckIn(options: ClearUnavailableCheckInOptions) {
  if (options.isFixedFlashLengthOfStayEnabled && options.checkIn && !options.checkOut) {
    return false
  }

  return !!(
    !options.fetching &&
    options.checkIn &&
    options.selectedDay &&
    !options.selectedDay.hotelAvailable &&
    !options.hasOtherAvailability
  )
}

export function getFixedFlashCalendarSelectionMode(options: FixedFlashCalendarSelectionModeOptions): FixedFlashCalendarSelectionMode {
  if (!options.isFixedFlashLengthOfStayEnabled || options.bundleOfferId) {
    return 'inactive'
  }
  if (options.selectedCheckIn && options.selectedCheckOut) {
    return 'selected-range'
  }
  if (options.selectedCheckIn) {
    return 'select-check-out'
  }

  return 'select-check-in'
}

export function getFixedFlashCalendarDayPricingState(options: FixedFlashCalendarDayPricingStateOptions) {
  const pricingDay = (options.isBeforeSelectedCheckIn && options.selectedCheckOut ? options.checkInCalendarDay : options.checkoutCalendarDay) ?? options.day
  const pricingDuration = pricingDay.checkOut ? moment(pricingDay.checkOut).diff(pricingDay.checkIn, 'days') : undefined
  const displayDuration = pricingDuration && pricingDuration > 0 ? pricingDuration : options.fallbackDuration

  return { pricingDay, displayDuration }
}

export function getFixedFlashCalendarDayState(options: FixedFlashCalendarDayStateOptions) {
  const {
    day,
    selectedCheckIn,
    selectedCheckOut,
    checkoutCalendarDays,
    checkInCalendarDays,
    isFixedFlashLengthOfStayDateSelection,
    minimumCheckoutDuration,
    maximumDuration,
  } = options
  const checkoutCalendarDay = checkoutCalendarDays[day.checkIn]
  const checkInCalendarDay = checkInCalendarDays[day.checkIn]
  const checkoutDuration = selectedCheckIn ? moment(day.checkIn).diff(selectedCheckIn, 'days') : undefined
  const checkInDuration = selectedCheckOut ? moment(selectedCheckOut).diff(day.checkIn, 'days') : undefined
  const isLaterThanSelectedCheckIn = checkoutDuration !== undefined && checkoutDuration > 0
  const isBeforeSelectedCheckIn = !!selectedCheckIn && moment(day.checkIn).isBefore(selectedCheckIn, 'day')
  const isMinimumStayCheckoutDisabled = !!(
    isFixedFlashLengthOfStayDateSelection &&
    minimumCheckoutDuration !== undefined &&
    isLaterThanSelectedCheckIn &&
    checkoutDuration < minimumCheckoutDuration
  )
  const isMaximumStayCheckInDisabled = !!(
    isFixedFlashLengthOfStayDateSelection &&
    selectedCheckOut &&
    isBeforeSelectedCheckIn &&
    maximumDuration !== undefined &&
    checkInDuration !== undefined &&
    checkInDuration > maximumDuration
  )
  const isMaximumStayCheckoutDisabled = !!(
    isFixedFlashLengthOfStayDateSelection &&
    maximumDuration !== undefined &&
    isLaterThanSelectedCheckIn &&
    checkoutDuration !== undefined &&
    checkoutDuration > maximumDuration
  )
  const isMaximumStayDisabled = isMaximumStayCheckInDisabled || isMaximumStayCheckoutDisabled
  const isUnavailableCheckoutCandidate = isLaterThanSelectedCheckIn && !checkoutCalendarDay
  const isUnavailableCheckInCandidate = !!selectedCheckOut && isBeforeSelectedCheckIn && !checkInCalendarDay
  const isFixedLengthCombinationUnavailable = !!(
    isFixedFlashLengthOfStayDateSelection &&
    !isMinimumStayCheckoutDisabled &&
    !isMaximumStayDisabled &&
    day.checkIn !== selectedCheckIn &&
    day.checkIn !== selectedCheckOut &&
    (isUnavailableCheckoutCandidate || isUnavailableCheckInCandidate)
  )

  return {
    checkoutCalendarDay,
    checkInCalendarDay,
    isFixedLengthCombinationUnavailable,
    isMinimumStayCheckoutDisabled,
    isMaximumStayDisabled,
  }
}
